import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { TranslationService } from '../../../services/translation/translation.service';
import { AngularEditorConfig } from '@wfpena/angular-wysiwyg';
import { EditorI18nService } from '../../../services/editor-i18n/editor-i18n.service';
import { UtilsService } from '../../../services/shared/utils/utils.service';
import { SelectOption } from '../../../models/selectOption.model';
import { MatSlideToggle } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-edit-option-modal',
  templateUrl: './edit-option-modal.component.html',
  styleUrl: './edit-option-modal.component.css'
})
export class EditOptionModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @ViewChild('autoResizeTextarea') textarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('jsonLabel') jsonLabel!: ElementRef<HTMLElement>;
  @ViewChild('editorTextArea', { read: ElementRef }) editorEl!: ElementRef<HTMLElement>;
  option: SelectOption;

  isJson: boolean = false;
  isJsonValid: boolean = true;

  translated = false;
  private _editorText = '';
  config: AngularEditorConfig = {
    editable: true,
    sanitize: false,
    outline: false,
    height: 'auto',
    minHeight: '75px',
    spellcheck: false,
    toolbarHiddenButtons: [
      ['insertVideo', 'insertImage', 'fontName']
    ],
    defaultFontSize: '2',
    defaultFontName:'"Poppins", sans-serif',
    enableToolbar: true,
    placeholder: ''
  };

  constructor(private dialogRef: MatDialogRef<EditOptionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { option: SelectOption },
    private editorI18n: EditorI18nService,
    private utils: UtilsService)
  {
    this.option = data.option;
  }

  ngOnInit()
  {
    //json
    this.isJson = this.utils.isValidJson(this.option.value);
    this.validateJson();
    this.beautifyJSON();

    //html
    if(!this.utils.containsHtml(this.option.display)){
      this.option.display = `<span style="font-size: small;">${this.option.display}</span>`
    }
    this._editorText = this.InitEditor(this.option.display);

    setTimeout(() => {
      this.adjustTextareaHeight();
    }, 0);
  }

  ngAfterViewChecked(): void {
    if (this.translated) return;

    const toolbarExists = document.querySelector('.angular-editor-toolbar');
    if (!toolbarExists) return;

    this.editorI18n.translateAll();
    this.translated = true;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent) {
    this.adjustTextareaHeight();
  }

  get editorText() {
    return this._editorText;
  }

  set editorText(value: string) {
    this._editorText = value;
    this.option.display = value;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    this.minifyJSON();
    this.dialogRef.close(this.option);
  }

  InitEditor(text: string | undefined): string {
    if (text && text.trim().length > 0) {
      return text + '';
    } else {
      return '<span style="font-size: small;"></span>';

    }
  }

  private isCleaning = false;
  onEditorChange(html: string): void {
    if (this.isCleaning) return;

    setTimeout(() => {
      const root = this.editorEl?.nativeElement;
      if (!root) return;

      const toggleBtn = root.querySelector('#toggleEditorMode-');
      if (toggleBtn?.classList.contains('active')) return;

      const editable = root.querySelector('.angular-editor-textarea') as HTMLElement;
      if (!editable) return;

      editable.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6')
        .forEach(h => {
          h.style.removeProperty('font-size');
          h.querySelectorAll<HTMLElement>('*')
            .forEach(child => child.style.removeProperty('font-size'));
        });

      this.isCleaning = true;
      this._editorText = editable.innerHTML;
      this.option.display = editable.innerHTML;
      this.isCleaning = false;
    });
  }

  validateJson() {
    const value = this.option?.value;

    if (!this.isJson || !value) {
      this.isJsonValid = true;
      return;
    }

    this.isJsonValid = this.utils.isValidJson(this.option.value);
  }

  adjustTextareaHeight(): void {
    //json side
    var jsonArea = this.textarea.nativeElement as HTMLElement;
    const jsonLabelHeight = (this.jsonLabel.nativeElement as HTMLElement).offsetHeight;
    const jsonContainerHeight = (jsonArea.parentElement as HTMLElement).offsetHeight;
    jsonArea.style.minHeight = `${jsonContainerHeight - jsonLabelHeight}px`;
    jsonArea.style.maxHeight = `${jsonContainerHeight - jsonLabelHeight}px`;
    jsonArea.style.height = `${jsonContainerHeight - jsonLabelHeight}px`;

    //html side
    var htmlArea = this.editorEl.nativeElement as HTMLElement;
    var textareaEl = htmlArea.querySelector('.angular-editor-textarea') as HTMLElement;

    const toolbarHeight = (htmlArea.querySelector('.angular-editor-toolbar') as HTMLElement).offsetHeight;
    const htmlLabelHeight = (this.jsonLabel.nativeElement as HTMLElement).offsetHeight;
    const htmlContainerHeight = (jsonArea.parentElement as HTMLElement).offsetHeight;

    textareaEl.style.minHeight = `${htmlContainerHeight - htmlLabelHeight - toolbarHeight}px`;
    textareaEl.style.maxHeight = `${htmlContainerHeight - htmlLabelHeight - toolbarHeight}px`;
    textareaEl.style.height = `${htmlContainerHeight - htmlLabelHeight - toolbarHeight}px`;
    textareaEl.style.resize = 'none';
  }

  beautifyJSON() {
    if(this.isJsonValid){
      this.option.value = this.utils.beautifyJSON(this.option.value) ?? this.option.value;
    }
  }

  minifyJSON() {
    if(this.isJsonValid){
      this.option.value = this.utils.minifyJSON(this.option.value) ?? this.option.value;
    }
  }
}
